import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminShell, type AdminSession } from '../admin/AdminShell'
import { PasswordField } from '../../components/PasswordField'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import {
  authModeToPath,
  normalizeEmail,
  parseAuthMode,
  type AuthMode,
  type LoginFormValues,
  type RecoveryFormValues,
  type ResetFormValues,
  validateLoginForm,
  validateRecoveryForm,
  validateResetForm,
} from './auth'

const logoUrl = '/logo.png'
const authStorageKey = 'fieldpresence.auth.session'
const apiBaseUrl = resolveApiBaseUrl()

type NoticeTone = 'idle' | 'success' | 'error' | 'warning'

type LoginApiError = {
  code?: string
  message?: string
}

type StoredSession = AdminSession

const loginDefaults: LoginFormValues = {
  email: '',
  password: '',
  rememberMe: true,
}

const recoveryDefaults: RecoveryFormValues = {
  email: '',
}

const resetDefaults: ResetFormValues = {
  token: '',
  password: '',
  confirmPassword: '',
}

function getInitialMode(): AuthMode {
  if (typeof window === 'undefined') {
    return 'login'
  }

  if (window.location.hash) {
    const modeFromHash = parseAuthMode(window.location.hash)
    window.history.replaceState(null, '', authModeToPath(modeFromHash))
    return modeFromHash
  }

  return parseAuthMode(window.location.pathname)
}

function resolveInitialMode(sessionExists: boolean): AuthMode {
  const initialMode = getInitialMode()

  if (!sessionExists) {
    return initialMode
  }

  if (
    initialMode === 'login' ||
    initialMode === 'recover' ||
    initialMode === 'reset' ||
    initialMode === 'error'
  ) {
    return 'dashboard'
  }

  return initialMode
}

function loadStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.localStorage.getItem(authStorageKey)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    if (!isStoredSession(parsed)) {
      clearStoredSession()
      return null
    }

    return parsed
  } catch {
    clearStoredSession()
    return null
  }
}

function saveStoredSession(session: StoredSession) {
  window.localStorage.setItem(authStorageKey, JSON.stringify(session))
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function isStoredSession(value: unknown): value is StoredSession {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<StoredSession>

  return Boolean(
    candidate.organization &&
      typeof candidate.organization.id === 'string' &&
      typeof candidate.organization.name === 'string' &&
      typeof candidate.organization.slug === 'string' &&
      candidate.user &&
      typeof candidate.user.id === 'string' &&
      typeof candidate.user.name === 'string' &&
      typeof candidate.user.email === 'string' &&
      typeof candidate.user.role === 'string' &&
      typeof candidate.user.status === 'string' &&
      isStringArray(candidate.user.permissions) &&
      isStringArray(candidate.user.rules) &&
      candidate.session &&
      typeof candidate.session.token === 'string' &&
      candidate.session.token.length > 0 &&
      typeof candidate.session.expiresAt === 'string',
  )
}

function clearStoredSession() {
  window.localStorage.removeItem(authStorageKey)
}

function resolveApiMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return fallback
    case 'VALIDATION_ERROR':
      return fallback
    default:
      return fallback
  }
}

export function AuthShell() {
  const { t } = useTranslation()
  const storedSession = loadStoredSession()
  const [mode, setMode] = useState<AuthMode>(() => {
    return resolveInitialMode(Boolean(storedSession))
  })
  const [noticeTone, setNoticeTone] = useState<NoticeTone>('idle')
  const [noticeMessage, setNoticeMessage] = useState('')
  const [loginValues, setLoginValues] = useState<LoginFormValues>(loginDefaults)
  const [recoveryValues, setRecoveryValues] = useState<RecoveryFormValues>(recoveryDefaults)
  const [resetValues, setResetValues] = useState<ResetFormValues>(resetDefaults)
  const [loginErrors, setLoginErrors] = useState<string[]>([])
  const [recoveryErrors, setRecoveryErrors] = useState<string[]>([])
  const [resetErrors, setResetErrors] = useState<string[]>([])
  const [selectedError, setSelectedError] = useState('invalidCredentials')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [session, setSession] = useState<StoredSession | null>(() => storedSession)

  useEffect(() => {
    const syncModeFromLocation = () => {
      if (window.location.hash) {
        const modeFromHash = parseAuthMode(window.location.hash)
        window.history.replaceState(null, '', authModeToPath(modeFromHash))
        setMode(modeFromHash)
      } else {
        setMode(parseAuthMode(window.location.pathname))
      }

      setNoticeTone('idle')
      setNoticeMessage('')
    }

    window.addEventListener('popstate', syncModeFromLocation)
    window.addEventListener('hashchange', syncModeFromLocation)
    return () => {
      window.removeEventListener('popstate', syncModeFromLocation)
      window.removeEventListener('hashchange', syncModeFromLocation)
    }
  }, [])

  useEffect(() => {
    if (
      session &&
      mode !== 'dashboard' &&
      mode !== 'profile' &&
      mode !== 'collaborators' &&
      mode !== 'trash' &&
      mode !== 'spaces' &&
      mode !== 'details' &&
      mode !== 'permissions' &&
      mode !== 'rules' &&
      mode !== 'settings'
    ) {
      setMode('dashboard')
      window.history.replaceState(null, '', authModeToPath('dashboard'))
      return
    }

    if (
      !session &&
      (
        mode === 'dashboard' ||
        mode === 'profile' ||
        mode === 'collaborators' ||
        mode === 'trash' ||
        mode === 'spaces' ||
        mode === 'details' ||
        mode === 'permissions' ||
        mode === 'rules' ||
        mode === 'settings'
      )
    ) {
      setMode('login')
      window.history.replaceState(null, '', authModeToPath('login'))
    }
  }, [mode, session])

  const navigateTo = (nextMode: AuthMode) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', authModeToPath(nextMode))
    }

    setMode(nextMode)
    setNoticeTone('idle')
    setNoticeMessage('')
  }

  const showStatus = (tone: NoticeTone, message: string) => {
    setNoticeTone(tone)
    setNoticeMessage(message)
  }

  const handleLogout = () => {
    clearStoredSession()
    setSession(null)
    setLoginValues((current) => ({ ...current, password: '' }))
    navigateTo('login')
  }

  const handleLoginSuccess = (payload: StoredSession) => {
    if (!isStoredSession(payload)) {
      clearStoredSession()
      setSession(null)
      showStatus('error', t('auth.api.unexpected'))
      navigateTo('login')
      return
    }

    setSession(payload)
    saveStoredSession(payload)
    navigateTo('dashboard')
  }

  const handleSessionUpdate = (payload: StoredSession) => {
    if (!isStoredSession(payload)) {
      handleLogout()
      return
    }

    setSession(payload)
    saveStoredSession(payload)
  }

  const syncSession = async () => {
    if (!isStoredSession(session)) {
      handleLogout()
      return
    }

    const response = await fetch(`${apiBaseUrl}/auth/session`, {
      headers: {
        Authorization: `Bearer ${session.session.token}`,
      },
    })

    const data = (await response.json().catch(() => null)) as StoredSession | LoginApiError | null

    if (!response.ok) {
      if (response.status === 401) {
        handleLogout()
        return
      }

      throw new Error(
        (data as LoginApiError | null)?.message || t('auth.api.unexpected'),
      )
    }

    if (!isStoredSession(data)) {
      handleLogout()
      return
    }

    const nextSession = data
    setSession(nextSession)
    saveStoredSession(nextSession)
  }

  useEffect(() => {
    if (!session) {
      return
    }

    let active = true

    const refresh = () => {
      if (!active || typeof document === 'undefined' || document.visibilityState !== 'visible') {
        return
      }

      void syncSession().catch((error) => {
        const message = error instanceof Error ? error.message : t('auth.api.unexpected')
        showStatus('warning', message)
      })
    }

    const onFocus = () => {
      refresh()
    }

    const onVisibilityChange = () => {
      refresh()
    }

    const intervalId = window.setInterval(refresh, 15000)

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    refresh()

    return () => {
      active = false
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isStoredSession(session) ? session.session.token : null])

  const performLogin = async (values: LoginFormValues) => {
    const response = await fetch(`${apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: normalizeEmail(values.email),
        password: values.password,
      }),
    })

    const data = (await response.json().catch(() => null)) as AdminSession | LoginApiError | null

    if (!response.ok) {
      const errorData = data as LoginApiError | null
      const fallbackMessage =
        response.status === 400
          ? t('auth.api.validationError')
          : response.status === 401
            ? t('auth.api.invalidCredentials')
            : t('auth.api.unexpected')
      throw new Error(resolveApiMessage(errorData?.code, fallbackMessage))
    }

    return data as StoredSession
  }

  const onLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const errors = validateLoginForm(loginValues)
    setLoginErrors(errors)

    if (errors.length > 0) {
      showStatus('error', t('auth.login.validationError'))
      return
    }

    setIsSubmitting(true)
    setNoticeTone('idle')
    setNoticeMessage('')

    try {
      const payload = await performLogin(loginValues)
      handleLoginSuccess(payload)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.api.unexpected')
      showStatus('error', message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onRecoverySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const errors = validateRecoveryForm(recoveryValues)
    setRecoveryErrors(errors)

    if (errors.length > 0) {
      showStatus('error', t('auth.recovery.validationError'))
      return
    }

    showStatus('success', t('auth.recovery.success'))
  }

  const onResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const errors = validateResetForm(resetValues)
    setResetErrors(errors)

    if (errors.length > 0) {
      showStatus('error', t('auth.reset.validationError'))
      return
    }

    showStatus('success', t('auth.reset.success'))
  }

  const errorPresets = [
    {
      key: 'invalidCredentials',
      title: t('auth.error.invalidCredentials.title'),
      description: t('auth.error.invalidCredentials.description'),
      resolution: t('auth.error.invalidCredentials.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'error' as const,
      actionLabel: t('auth.error.actions.backToLogin'),
      actionMode: 'login' as AuthMode,
    },
    {
      key: 'accountLocked',
      title: t('auth.error.accountLocked.title'),
      description: t('auth.error.accountLocked.description'),
      resolution: t('auth.error.accountLocked.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'warning' as const,
      actionLabel: t('auth.error.actions.goRecovery'),
      actionMode: 'recover' as AuthMode,
    },
    {
      key: 'sessionExpired',
      title: t('auth.error.sessionExpired.title'),
      description: t('auth.error.sessionExpired.description'),
      resolution: t('auth.error.sessionExpired.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'warning' as const,
      actionLabel: t('auth.error.actions.backToLogin'),
      actionMode: 'login' as AuthMode,
    },
    {
      key: 'tokenExpired',
      title: t('auth.error.tokenExpired.title'),
      description: t('auth.error.tokenExpired.description'),
      resolution: t('auth.error.tokenExpired.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'warning' as const,
      actionLabel: t('auth.error.actions.goRecovery'),
      actionMode: 'recover' as AuthMode,
    },
    {
      key: 'tooManyAttempts',
      title: t('auth.error.tooManyAttempts.title'),
      description: t('auth.error.tooManyAttempts.description'),
      resolution: t('auth.error.tooManyAttempts.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'error' as const,
      actionLabel: t('auth.error.actions.backToLogin'),
      actionMode: 'login' as AuthMode,
    },
    {
      key: 'networkFailure',
      title: t('auth.error.networkFailure.title'),
      description: t('auth.error.networkFailure.description'),
      resolution: t('auth.error.networkFailure.resolution'),
      badge: t('auth.error.badge.common'),
      tone: 'warning' as const,
      actionLabel: t('auth.error.actions.retry'),
      actionMode: 'login' as AuthMode,
    },
    {
      key: 'serverFailure',
      title: t('auth.error.serverFailure.title'),
      description: t('auth.error.serverFailure.description'),
      resolution: t('auth.error.serverFailure.resolution'),
      badge: t('auth.error.badge.system'),
      tone: 'error' as const,
      actionLabel: t('auth.error.actions.retry'),
      actionMode: 'login' as AuthMode,
    },
    {
      key: 'generic',
      title: t('auth.error.generic.title'),
      description: t('auth.error.generic.description'),
      resolution: t('auth.error.generic.resolution'),
      badge: t('auth.error.badge.fallback'),
      tone: 'error' as const,
      actionLabel: t('auth.error.actions.backToLogin'),
      actionMode: 'login' as AuthMode,
    },
  ] as const

  const activeError = errorPresets.find((preset) => preset.key === selectedError) ?? errorPresets[0]

  const renderStatusNotice = () => {
    if (!noticeMessage) {
      return null
    }

    return (
      <div className={`auth-notice auth-notice--${noticeTone}`} role="status">
        {noticeMessage}
      </div>
    )
  }

  const renderSystemErrorPanel = () => (
    <div className="auth-system-error">
      <div className="auth-error-hub">
        <div className="auth-error-hub__banner">
          <p className="auth-error-hub__eyebrow">{t('auth.error.label')}</p>
          <h2>{t('auth.error.description')}</h2>
          <p>{t('auth.error.generic.description')}</p>
        </div>

        <div className={`auth-notice auth-notice--${activeError.tone}`} role="status">
          <strong>{activeError.title}</strong>
          <p>{activeError.description}</p>
          <small>{activeError.resolution}</small>
        </div>

        <div className="auth-error-grid">
          {errorPresets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={`auth-error-card${selectedError === preset.key ? ' auth-error-card--active' : ''}`}
              onClick={() => {
                setSelectedError(preset.key)
                showStatus(preset.tone, preset.description)
              }}
            >
              <span className="auth-error-card__badge">{preset.badge}</span>
              <strong>{preset.title}</strong>
              <p>{preset.description}</p>
              <small>{preset.resolution}</small>
            </button>
          ))}
        </div>

        <div className="auth-error-actions">
          <button
            type="button"
            className="auth-primary"
            onClick={() => navigateTo(activeError.actionMode)}
          >
            {activeError.actionLabel}
          </button>
          <button type="button" className="auth-link" onClick={() => navigateTo('login')}>
            {t('auth.error.actions.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  )

  const renderLoginForm = () => (
    <form className="auth-form" onSubmit={onLoginSubmit}>
      <label>
        <span>{t('auth.login.email')}</span>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={loginValues.email}
          onChange={(event) =>
            setLoginValues((current) => ({ ...current, email: event.target.value }))
          }
          placeholder="nome@empresa.com"
        />
        {loginErrors.includes('email') ? <small>{t('auth.login.error.email')}</small> : null}
      </label>

      <PasswordField
        label={t('auth.login.password')}
        value={loginValues.password}
        onChange={(value) => setLoginValues((current) => ({ ...current, password: value }))}
        autoComplete="current-password"
        placeholder="••••••••"
        error={loginErrors.includes('password') ? t('auth.login.error.password') : undefined}
        variant="auth"
      />

      <div className="auth-form__row">
        <label className="auth-check">
          <input
            type="checkbox"
            checked={loginValues.rememberMe}
            onChange={(event) =>
              setLoginValues((current) => ({ ...current, rememberMe: event.target.checked }))
            }
          />
          <span>{t('auth.login.rememberMe')}</span>
        </label>
      </div>

      <button type="submit" className="auth-primary" disabled={isSubmitting}>
        {isSubmitting ? t('common.loading') : t('auth.login.submit')}
      </button>

      <button type="button" className="auth-link auth-link--center" onClick={() => navigateTo('recover')}>
        {t('auth.login.forgotPassword')}
      </button>
    </form>
  )

  const renderRecoveryForm = () => (
    <form className="auth-form" onSubmit={onRecoverySubmit}>
      <label>
        <span>{t('auth.recovery.email')}</span>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={recoveryValues.email}
          onChange={(event) =>
            setRecoveryValues((current) => ({ ...current, email: event.target.value }))
          }
          placeholder="nome@empresa.com"
        />
        {recoveryErrors.includes('email') ? <small>{t('auth.recovery.error.email')}</small> : null}
      </label>

      <button type="submit" className="auth-primary auth-primary--block" disabled={isSubmitting}>
        {isSubmitting ? t('common.loading') : t('auth.recovery.submit')}
      </button>

      <button type="button" className="auth-link auth-link--center" onClick={() => navigateTo('login')}>
        {t('auth.recovery.backToLogin')}
      </button>
    </form>
  )

  const renderResetForm = () => (
    <form className="auth-form" onSubmit={onResetSubmit}>
      <label>
        <span>{t('auth.reset.token')}</span>
        <input
          type="text"
          autoComplete="one-time-code"
          value={resetValues.token}
          onChange={(event) =>
            setResetValues((current) => ({ ...current, token: event.target.value }))
          }
          placeholder="TOKEN-123"
        />
        {resetErrors.includes('token') ? <small>{t('auth.reset.error.token')}</small> : null}
      </label>

      <PasswordField
        label={t('auth.reset.password')}
        value={resetValues.password}
        onChange={(value) => setResetValues((current) => ({ ...current, password: value }))}
        autoComplete="new-password"
        placeholder="••••••••"
        error={resetErrors.includes('password') ? t('auth.reset.error.password') : undefined}
        variant="auth"
      />

      <PasswordField
        label={t('auth.reset.confirmPassword')}
        value={resetValues.confirmPassword}
        onChange={(value) =>
          setResetValues((current) => ({ ...current, confirmPassword: value }))
        }
        autoComplete="new-password"
        placeholder="••••••••"
        error={
          resetErrors.includes('confirmPassword')
            ? t('auth.reset.error.confirmPassword')
            : resetErrors.includes('passwordMismatch')
              ? t('auth.reset.error.passwordMismatch')
              : undefined
        }
        variant="auth"
      />

      <div className="auth-form__row">
        <button type="button" className="auth-link" onClick={() => navigateTo('recover')}>
          {t('auth.reset.backToRecovery')}
        </button>

        <button type="submit" className="auth-primary" disabled={isSubmitting}>
          {isSubmitting ? t('common.loading') : t('auth.reset.submit')}
        </button>
      </div>
    </form>
  )

  const currentMode = session
    ? mode === 'profile' ||
      mode === 'collaborators' ||
      mode === 'trash' ||
      mode === 'spaces' ||
      mode === 'details' ||
      mode === 'permissions' ||
      mode === 'rules' ||
      mode === 'settings'
      ? mode
      : 'dashboard'
    : mode

  useEffect(() => {
    let title = 'MarcaEu'
    switch (currentMode) {
      case 'dashboard':
        title = 'Painel - MarcaEu'
        break
      case 'profile':
        title = 'Perfil - MarcaEu'
        break
      case 'collaborators':
        title = 'Colaboradores - MarcaEu'
        break
      case 'trash':
        title = 'Lixeira - MarcaEu'
        break
      case 'spaces':
        title = 'Espaços - MarcaEu'
        break
      case 'details':
        title = 'Detalhes - MarcaEu'
        break
      case 'permissions':
        title = 'Permissões - MarcaEu'
        break
      case 'rules':
        title = 'Papéis - MarcaEu'
        break
      case 'settings':
        title = 'Configurações - MarcaEu'
        break
      case 'login':
        title = 'Login - MarcaEu'
        break
      case 'recover':
        title = 'Recuperação - MarcaEu'
        break
      case 'reset':
        title = 'Redefinição - MarcaEu'
        break
      case 'error':
        title = 'Erro - MarcaEu'
        break
    }
    document.title = title
  }, [currentMode])

  if (
    session &&
    (currentMode === 'dashboard' ||
      currentMode === 'profile' ||
      currentMode === 'collaborators' ||
      currentMode === 'trash' ||
      currentMode === 'spaces' ||
      currentMode === 'details' ||
      currentMode === 'permissions' ||
      currentMode === 'rules' ||
      currentMode === 'settings')
  ) {
    return (
      <AdminShell
        session={session}
        view={currentMode}
        onNavigate={(nextView) => navigateTo(nextView)}
        onLogout={handleLogout}
        onSessionUpdate={handleSessionUpdate}
      />
    )
  }

  return (
    <div className="auth-page">
      <section className="auth-shell">
        {mode === 'error' ? renderSystemErrorPanel() : null}

        {mode !== 'error' ? (
          <div className="auth-panel__surface">
            <header className="auth-panel__header">
              <div className="auth-brand">
                <div className="auth-brand__copy">
                  <h1>{t(`auth.mode.${mode}`)}</h1>
                </div>
                <div className="auth-brand__mark">
                  <img src={logoUrl} alt={t('app.title')} />
                </div>
              </div>
            </header>

            {renderStatusNotice()}

            <div className="auth-palette" aria-hidden="true">
              <span className="auth-palette__swatch auth-palette__swatch--purple" />
              <span className="auth-palette__swatch auth-palette__swatch--orange" />
              <span className="auth-palette__swatch auth-palette__swatch--white" />
            </div>

            {mode === 'login' ? renderLoginForm() : null}
            {mode === 'recover' ? renderRecoveryForm() : null}
            {mode === 'reset' ? renderResetForm() : null}
          </div>
        ) : null}
      </section>
    </div>
  )
}
